import { useEffect, useMemo, useState } from "react";
import { Instagram, ArrowUpRight, BookOpen, Mail, Sparkles, Check, Loader2, Search, X, Star, Clock, Tag as TagIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const INSTAGRAM_URL = "https://instagram.com/vibewithsimm";
const CALENDLY_URL = "https://calendly.com/simmbedi-sb/30-min-discovery-call";
const RECENT_KEY = "bts-recent-searches";
const MAX_RECENT = 3;

function loadRecent() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function saveRecent(term) {
  const t = term.trim();
  if (!t || t.length < 2) return loadRecent();
  const prev = loadRecent().filter((x) => x.toLowerCase() !== t.toLowerCase());
  const next = [t, ...prev].slice(0, MAX_RECENT);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

function FeaturedCard({ guide, onOpen, onTagClick }) {
  const tags = guide.tags || [];
  return (
    <article
      data-testid={`featured-guide-card-${guide.id}`}
      className="group relative overflow-hidden rounded-3xl border border-burgundy/20 bg-gradient-to-br from-blush via-white to-cream p-8 md:p-12 shadow-editorial rise-in"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-burgundy/10 blur-3xl"
      />
      <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-burgundy px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white">
            <Star className="h-3 w-3 fill-white" /> Featured this week
          </span>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-burgundy-ink">
            {guide.title}
          </h2>
          <p className="mt-4 text-[16px] md:text-lg leading-relaxed text-foreground/75 max-w-xl">
            {guide.description}
          </p>
          {tags.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`featured-tag-${t}`}
                  onClick={() => onTagClick?.(t)}
                  className="inline-flex items-center gap-1 rounded-full border border-burgundy/20 bg-white px-3 py-1 text-[11px] font-medium text-burgundy hover:bg-burgundy hover:text-white transition-colors"
                >
                  #{t}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
          <Button
            data-testid={`get-featured-guide-btn-${guide.id}`}
            onClick={() => onOpen(guide)}
            className="rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-12 px-7 text-[15px] font-medium shadow-sm"
          >
            Get the featured guide
            <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Button>
          <span className="text-xs uppercase tracking-[0.16em] text-burgundy/60">
            Editor's pick · PDF · instant
          </span>
        </div>
      </div>
    </article>
  );
}

function GuideCard({ guide, index, onOpen, onTagClick }) {
  const tags = guide.tags || [];
  return (
    <article
      data-testid={`guide-card-${guide.id}`}
      className="group relative flex flex-col justify-between rounded-2xl bg-white border border-sand p-7 md:p-8 shadow-editorial transition-transform duration-300 hover:-translate-y-1 rise-in"
      style={{ animationDelay: `${index * 90}ms` }}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blush px-3 py-1 text-[11px] font-medium uppercase tracking-[0.14em] text-burgundy">
          <BookOpen className="h-3 w-3" /> Guide {String(index + 1).padStart(2, "0")}
        </span>
        <span className="font-heading text-xs italic text-burgundy/60">free</span>
      </div>

      <div className="mt-6">
        <h2 className="font-heading text-[26px] md:text-3xl leading-[1.1] text-burgundy-ink">
          {guide.title}
        </h2>
        <p className="mt-3 text-[15px] md:text-base leading-relaxed text-foreground/75">
          {guide.description}
        </p>
        {tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`guide-tags-${guide.id}`}>
            {tags.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`guide-tag-${guide.id}-${t}`}
                onClick={(e) => { e.stopPropagation(); onTagClick?.(t); }}
                className="inline-flex items-center gap-1 rounded-full border border-sand bg-cream px-2.5 py-0.5 text-[11px] font-medium text-burgundy/80 hover:bg-blush hover:text-burgundy hover:border-blush transition-colors"
              >
                #{t}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Button
          data-testid={`get-guide-btn-${guide.id}`}
          onClick={() => onOpen(guide)}
          className="rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-11 px-5 font-medium tracking-wide shadow-sm"
        >
          Get the guide
          <ArrowUpRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Button>
        <span className="text-xs text-foreground/50">PDF · instant</span>
      </div>

      <div className="pointer-events-none absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-blush to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    </article>
  );
}

function EmailCaptureModal({ open, onOpenChange, guide }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open, guide?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg("Please enter a valid email address.");
      return;
    }
    setErrorMsg("");
    setStatus("loading");
    try {
      await api.post(`/guides/${guide.id}/request`, { email });
      setStatus("done");
      toast.success("Sent! Check your inbox.");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-capture-modal"
        className="sm:max-w-md bg-cream border border-sand rounded-2xl p-0 overflow-hidden"
      >
        {status !== "done" ? (
          <div className="p-7 md:p-8">
            <DialogHeader className="text-left space-y-2">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-burgundy">
                {guide?.title}
              </span>
              <DialogTitle className="font-heading text-2xl md:text-3xl text-burgundy-ink leading-tight">
                Enter your email to get instant access.
              </DialogTitle>
              <DialogDescription className="text-foreground/70 text-[14px]">
                We'll send the PDF straight to your inbox — no spam, ever.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <label className="block">
                <span className="sr-only">Email address</span>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-burgundy/60" />
                  <Input
                    data-testid="email-input"
                    type="email"
                    required
                    autoFocus
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 rounded-xl bg-white border-sand focus-visible:ring-2 focus-visible:ring-burgundy focus-visible:ring-offset-0"
                  />
                </div>
              </label>
              {errorMsg && (
                <p data-testid="email-error" className="text-sm text-red-700">{errorMsg}</p>
              )}
              <Button
                data-testid="submit-email-btn"
                type="submit"
                disabled={status === "loading"}
                className="w-full h-12 rounded-xl bg-burgundy hover:bg-burgundy-700 text-white text-[15px] font-medium"
              >
                {status === "loading" ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>
                ) : (
                  <>Send it to me <ArrowUpRight className="h-4 w-4" /></>
                )}
              </Button>
              <p className="text-center text-xs text-foreground/50 pt-1">
                By submitting, you agree to occasional emails from Simm. Unsubscribe anytime.
              </p>
            </form>
          </div>
        ) : (
          <div data-testid="confirmation-screen" className="p-8 md:p-10 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-blush">
              <Check className="h-7 w-7 text-burgundy" strokeWidth={2.5} />
            </div>
            <h3 className="font-heading text-3xl text-burgundy-ink leading-tight">
              Check your inbox — <span className="italic">it's on its way.</span>
            </h3>
            <p className="mt-3 text-foreground/70 text-[15px]">
              We sent <strong className="text-burgundy">{guide?.title}</strong> to <em>{email}</em>.
              Not there in a few minutes? Peek in Promotions or Spam.
            </p>
            <Button
              data-testid="confirmation-close-btn"
              onClick={() => onOpenChange(false)}
              className="mt-7 rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-11 px-6"
            >
              Back to the guides
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Home() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeGuide, setActiveGuide] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [recent, setRecent] = useState(() => loadRecent());
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/guides")
      .then((res) => { if (!cancelled) setGuides(res.data); })
      .catch(() => { if (!cancelled) toast.error("Couldn't load guides"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Debounced save of the current query into recent searches
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) return;
    const t = setTimeout(() => setRecent(saveRecent(q)), 900);
    return () => clearTimeout(t);
  }, [query]);

  const openGuide = (g) => { setActiveGuide(g); setModalOpen(true); };

  const featured = useMemo(
    () => guides.find((g) => g.is_featured) || null,
    [guides]
  );
  const nonFeatured = useMemo(
    () => guides.filter((g) => !g.is_featured),
    [guides]
  );

  const allTags = useMemo(() => {
    const set = new Set();
    guides.forEach((g) => (g.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [guides]);

  const filteredGuides = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? guides : nonFeatured;
    if (q) {
      list = list.filter((g) =>
        (g.title || "").toLowerCase().includes(q) ||
        (g.description || "").toLowerCase().includes(q) ||
        (g.tags || []).some((t) => t.toLowerCase().includes(q))
      );
    }
    if (activeTag) {
      list = list.filter((g) => (g.tags || []).includes(activeTag));
    }
    return list;
  }, [guides, nonFeatured, query, activeTag]);

  const scrollToLibrary = () => {
    const el = document.getElementById("guides");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const applyRecent = (term) => {
    setQuery(term);
    setActiveTag("");
    setSearchFocused(false);
    scrollToLibrary();
  };

  const applyTag = (tag) => {
    setActiveTag((prev) => (prev === tag ? "" : tag));
    setQuery("");
    scrollToLibrary();
  };

  const clearRecent = () => {
    try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ }
    setRecent([]);
  };

  return (
    <div data-testid="home-page" className="min-h-screen bg-cream text-foreground">
      {/* Top nav */}
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 md:px-8 pt-7">
        <a href="/" className="flex shrink-0 items-center gap-2 group">
          <span className="h-2.5 w-2.5 rounded-full bg-burgundy" />
          <span className="font-heading text-[15px] italic tracking-wide text-burgundy">Behind the Story</span>
        </a>

        <div className="relative flex-1 max-w-xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-burgundy/60 z-10" />
          <Input
            data-testid="header-search-input"
            type="search"
            value={query}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
            onChange={(e) => {
              setQuery(e.target.value);
              if (e.target.value) scrollToLibrary();
            }}
            placeholder="Search guides by title or keyword…"
            aria-label="Search guides"
            className="h-11 md:h-12 pl-11 pr-10 rounded-full bg-white border-sand text-[15px] shadow-editorial placeholder:text-foreground/45 focus-visible:ring-2 focus-visible:ring-burgundy focus-visible:ring-offset-0"
          />
          {query && (
            <button
              type="button"
              aria-label="Clear search"
              data-testid="header-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-burgundy/60 hover:text-burgundy hover:bg-blush transition-colors z-10"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Recent searches dropdown */}
          {searchFocused && !query && recent.length > 0 && (
            <div
              data-testid="recent-searches"
              className="absolute left-0 right-0 mt-2 rounded-2xl border border-sand bg-white shadow-editorial p-2 z-20 rise-in"
            >
              <div className="flex items-center justify-between px-3 pt-1.5 pb-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-burgundy/70">
                  <Clock className="inline h-3 w-3 mr-1 -mt-0.5" /> Recent
                </span>
                <button
                  type="button"
                  data-testid="clear-recent-btn"
                  onMouseDown={(e) => { e.preventDefault(); clearRecent(); }}
                  className="text-[10px] uppercase tracking-[0.14em] text-foreground/50 hover:text-burgundy transition-colors"
                >
                  Clear
                </button>
              </div>
              <ul className="flex flex-col">
                {recent.map((term) => (
                  <li key={term}>
                    <button
                      type="button"
                      data-testid={`recent-search-${term}`}
                      onMouseDown={(e) => { e.preventDefault(); applyRecent(term); }}
                      className="w-full flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[14px] text-burgundy-ink hover:bg-blush/70 transition-colors"
                    >
                      <Search className="h-3.5 w-3.5 text-burgundy/50 shrink-0" />
                      <span className="truncate">{term}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <a
          data-testid="nav-instagram"
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex shrink-0 items-center gap-1.5 text-xs tracking-[0.14em] uppercase text-burgundy/70 hover:text-burgundy transition-colors"
        >
          <Instagram className="h-3.5 w-3.5" /> @vibewithsimm
        </a>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-5 md:px-8 pt-14 md:pt-24 pb-10 md:pb-16">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-sand bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-burgundy">
            <Sparkles className="h-3 w-3" /> Free resource library
          </div>
          <h1
            data-testid="hero-headline"
            className="mt-6 font-heading text-[44px] sm:text-6xl lg:text-[80px] leading-[0.95] tracking-tight text-burgundy-ink"
          >
            Behind <span className="italic text-burgundy">the</span> <span className="underline-flourish">Story</span>.
          </h1>
          <p
            data-testid="hero-subhead"
            className="mt-6 max-w-2xl text-[17px] md:text-xl leading-relaxed text-foreground/75"
          >
            Free frameworks and guides for building a personal brand that
            <span className="italic"> actually </span> converts.
          </p>
        </div>
      </section>

      {/* Guides grid */}
      <section
        id="guides"
        className="mx-auto max-w-6xl px-5 md:px-8 pb-24"
      >
        {featured && !query && !activeTag && (
          <div className="mb-10 md:mb-14">
            <FeaturedCard guide={featured} onOpen={openGuide} onTagClick={applyTag} />
          </div>
        )}

        {allTags.length > 0 && (
          <div
            data-testid="tag-filter-bar"
            className="mb-8 md:mb-10 flex flex-wrap items-center gap-2"
          >
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-burgundy/60 mr-1">
              <TagIcon className="h-3 w-3" /> Filter by topic
            </span>
            <button
              type="button"
              data-testid="tag-filter-all"
              onClick={() => setActiveTag("")}
              className={
                (activeTag === ""
                  ? "bg-burgundy text-white border-burgundy"
                  : "bg-white text-burgundy/80 border-sand hover:bg-blush hover:border-blush") +
                " inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-medium transition-colors"
              }
            >
              All
            </button>
            {allTags.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`tag-filter-${t}`}
                onClick={() => applyTag(t)}
                className={
                  (activeTag === t
                    ? "bg-burgundy text-white border-burgundy"
                    : "bg-white text-burgundy/80 border-sand hover:bg-blush hover:border-blush") +
                  " inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-medium transition-colors"
                }
              >
                #{t}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-end justify-between mb-8 md:mb-10">
          <h2 className="font-heading text-2xl md:text-3xl text-burgundy-ink">
            {query || activeTag ? "Results" : "The library"}
          </h2>
          <span data-testid="library-count" className="text-xs uppercase tracking-[0.18em] text-foreground/50 hidden sm:inline">
            {query
              ? `${filteredGuides.length} match${filteredGuides.length === 1 ? "" : "es"} for "${query}"`
              : activeTag
                ? `${filteredGuides.length} in #${activeTag}`
                : `${guides.length} ${guides.length === 1 ? "guide" : "guides"} · new drops soon`}
          </span>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {[0, 1].map((i) => (
              <div key={i} className="h-64 rounded-2xl bg-white border border-sand animate-pulse" />
            ))}
          </div>
        ) : filteredGuides.length === 0 ? (
          <div data-testid="empty-state" className="rounded-2xl border border-dashed border-sand p-10 text-center bg-white/60">
            <p className="font-heading text-2xl text-burgundy-ink">
              {query ? "No guides match that yet." : "New guides coming soon."}
            </p>
            <p className="mt-2 text-foreground/60">
              {query ? "Try a different keyword — or " : "Follow along on Instagram for the drops."}
              {query && (
                <button
                  data-testid="clear-search-btn"
                  onClick={() => setQuery("")}
                  className="underline text-burgundy hover:text-burgundy-700"
                >
                  clear the search
                </button>
              )}
              {query ? "." : ""}
            </p>
          </div>
        ) : (
          <div
            data-testid="guides-grid"
            className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
          >
            {filteredGuides.map((g, i) => (
              <GuideCard key={g.id} guide={g} index={i} onOpen={openGuide} onTagClick={applyTag} />
            ))}
          </div>
        )}
      </section>

      {/* Footer */}
      <footer data-testid="site-footer" className="border-t border-sand bg-white/60">
        <div className="mx-auto max-w-6xl px-5 md:px-8 py-14 md:py-20 grid gap-10 md:grid-cols-3">
          <div className="md:col-span-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-burgundy">Want more?</p>
            <h3 className="mt-3 font-heading text-3xl md:text-5xl leading-[1.05] text-burgundy-ink max-w-xl">
              Let's build the brand your work actually deserves.
            </h3>
            <p className="mt-4 max-w-lg text-foreground/70">
              Book a 30-min discovery call and walk away with a clearer POV, better hooks, and a plan you can post this week.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="footer-book-call-btn"
                  className="rounded-full bg-burgundy hover:bg-burgundy-700 text-white h-12 px-6 text-[15px] font-medium"
                >
                  Book a discovery call
                  <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                <Button
                  data-testid="footer-instagram-btn"
                  variant="outline"
                  className="rounded-full border-sand bg-white hover:bg-blush hover:text-burgundy h-12 px-6 text-[15px] font-medium text-burgundy"
                >
                  <Instagram className="h-4 w-4" /> @vibewithsimm
                </Button>
              </a>
            </div>
          </div>
          <div className="flex md:justify-end md:items-end">
            <div className="text-sm text-foreground/60">
              <p className="font-heading text-lg text-burgundy-ink">Behind the Story</p>
              <p className="mt-2">© {new Date().getFullYear()} Simm Bedi</p>
              <p className="mt-1">
                <a
                  href="/admin/login"
                  data-testid="admin-login-link"
                  className="hover:text-burgundy transition-colors"
                >
                  Admin
                </a>
              </p>
            </div>
          </div>
        </div>
      </footer>

      <EmailCaptureModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        guide={activeGuide}
      />
    </div>
  );
}
